'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { SIDEBAR_COLLAPSED_STORAGE_KEY, SIDEBAR_WIDTH_EXPANDED_PX, SIDEBAR_WIDTH_COLLAPSED_PX } from '@/lib/layout/railsPrePaint';

// The key and widths live in railsPrePaint.ts so the <head> pre-paint script reserves exactly what this settles on.
const STORAGE_KEY = SIDEBAR_COLLAPSED_STORAGE_KEY;

export const SIDEBAR_WIDTH_EXPANDED = 280;
export const SIDEBAR_WIDTH_COLLAPSED = SIDEBAR_WIDTH_COLLAPSED_PX;
/** The sidebar IS the WatchlistPane site-wide (owner sign-off 2026-07-07) — live quote
 * rows + sparklines need more room than the old 280px menu. */
export const SIDEBAR_WIDTH_CHART = SIDEBAR_WIDTH_EXPANDED_PX;

interface SidebarContextType {
  expanded: boolean;
  setExpanded: (v: boolean) => void;
  toggle: () => void;
  width: number;
  /** False until the stored collapsed state has been read. Before that `expanded` is only the SSR default, so
   * nothing may publish a width from it (the pre-paint script already set the real one). */
  restored: boolean;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export function useSidebar() {
  const ctx = useContext(SidebarContext);
  if (ctx === undefined) {
    throw new Error('useSidebar must be used within SidebarProvider');
  }
  return ctx;
}

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [expanded, setExpandedState] = useState(true);
  const [restored, setRestored] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = localStorage.getItem(STORAGE_KEY);
    setExpandedState(stored !== 'true');
    setRestored(true);
  }, []);

  const setExpanded = useCallback((value: boolean) => {
    setExpandedState(value);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, value ? 'false' : 'true');
    }
  }, []);

  const toggle = useCallback(() => {
    setExpandedState((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, next ? 'false' : 'true');
      }
      return next;
    });
  }, []);

  const width = expanded ? SIDEBAR_WIDTH_CHART : SIDEBAR_WIDTH_COLLAPSED;

  return (
    <SidebarContext.Provider value={{ expanded, setExpanded, toggle, width, restored }}>
      {children}
    </SidebarContext.Provider>
  );
}
